import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { PageLoader } from './components/common/PageLoader';
import { RootLayout } from './components/layout/RootLayout';
import { RouteError } from './components/layout/RouteError';
import { AdminProvider } from './features/admin/AdminProvider';
import HomePage from './pages/HomePage';

/*
 * The home page ships in the main bundle because it is the most common entry
 * point. Everything else is split, so a first-time visitor does not download
 * the checkout form just to read the hero.
 */
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const BrandsPage = lazy(() => import('./pages/BrandsPage'));
const HowToBuyPage = lazy(() => import('./pages/HowToBuyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPage = lazy(() =>
  import('./pages/LegalPages').then((module) => ({ default: module.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import('./pages/LegalPages').then((module) => ({ default: module.TermsPage })),
);

/* Painel administrativo: bundle próprio, nunca baixado por quem só visita a loja. */
const AdminShell = lazy(() =>
  import('./components/admin/AdminShell').then((m) => ({ default: m.AdminShell })),
);
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage'));
const AdminProductsPage = lazy(() => import('./pages/admin/AdminProductsPage'));
const AdminBrandsPage = lazy(() => import('./pages/admin/AdminBrandsPage'));
const AdminCategoriesPage = lazy(() => import('./pages/admin/AdminCategoriesPage'));
const AdminFaqPage = lazy(() => import('./pages/admin/AdminFaqPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));

/** Wraps a lazy route element in its loading boundary. */
function withSuspense(element: ReactNode): ReactNode {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'catalogo', element: withSuspense(<CatalogPage />) },
      { path: 'produto/:slug', element: withSuspense(<ProductPage />) },
      { path: 'marcas', element: withSuspense(<BrandsPage />) },
      { path: 'como-comprar', element: withSuspense(<HowToBuyPage />) },
      { path: 'contato', element: withSuspense(<ContactPage />) },
      // Link antigo: o checkout virou envio direto da lista pelo WhatsApp.
      { path: 'checkout', element: <Navigate to="/catalogo" replace /> },
      { path: 'favoritos', element: withSuspense(<FavoritesPage />) },
      { path: 'privacidade', element: withSuspense(<PrivacyPage />) },
      { path: 'termos', element: withSuspense(<TermsPage />) },
      { path: '*', element: withSuspense(<NotFoundPage />) },
    ],
  },

  {
    // Fora do RootLayout: o painel tem cabeçalho, menu e largura próprios.
    path: '/admin',
    element: withSuspense(
      <AdminProvider>
        <AdminShell />
      </AdminProvider>,
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: withSuspense(<AdminOverviewPage />) },
      { path: 'produtos', element: withSuspense(<AdminProductsPage />) },
      { path: 'marcas', element: withSuspense(<AdminBrandsPage />) },
      { path: 'categorias', element: withSuspense(<AdminCategoriesPage />) },
      { path: 'perguntas', element: withSuspense(<AdminFaqPage />) },
      { path: 'configuracoes', element: withSuspense(<AdminSettingsPage />) },
    ],
  },
]);
